/**
 * Universal RUL API Service
 *
 * This service can fetch RUL forms from ANY backend service URL.
 * It doesn't care about the service name - it just fetches the form structure
 * and lets the UniversalRULForm component handle rendering.
 *
 * Philosophy: Backend owns everything. Frontend just renders.
 */

import apiClient from '../client';

/**
 * Patch trigger type - when to send PATCH request
 */
export type PatchTrigger = 'blur' | 'change' | 'keyup';

/**
 * Form meta info from backend - contains URL patterns and defaults
 */
export interface FormMeta {
  patch_url: string; // e.g., "/calendars/forms/flight-scheduler/event/field/{field_name}"
  submit_url: string; // e.g., "/calendars/forms/flight-scheduler/event"
  default_patch_trigger?: PatchTrigger; // Default trigger for all fields (default: 'blur')
}

/**
 * Generic RUL form response - supports both formats:
 * 1. Hierarchical: { rows: [...] }
 * 2. Flat: { __root: [...], field1: {...}, field2: {...} }
 */
export interface UniversalRULFormResponse {
  rows?: Array<{
    columns: Array<{
      widgets: any[];
      width?: number;
    }>;
  }>;
  __root?: string[];
  __form_meta?: FormMeta;
  [key: string]: any;
}

/**
 * Field update response
 * Can be:
 * 1. Nested format: { form: { __root: [...], ... }, updated_fields, validation_errors }
 * 2. Hierarchical: { rows: [...], updated_fields, validation_errors }
 * 3. Flat format: { __root: [...], field1: {...}, updated_fields, validation_errors }
 */
export interface UniversalRULUpdateResponse {
  form?: UniversalRULFormResponse;
  rows?: any[];
  __root?: string[];
  updated_fields?: string[];
  validation_errors?: Record<string, string>;
  [key: string]: any; // Allow flat format fields
}

/**
 * Form submit response
 */
export interface UniversalRULSubmitResponse {
  success: boolean;
  data?: any;
  message?: string;
  validation_errors?: Record<string, string>;
}

/**
 * Get form from any URL
 *
 * @param formUrl - Full or relative URL to form (e.g., "/formsexamples/rul/forms/text_input_showcase")
 * @returns Form structure (auto-detects format)
 */
export const getForm = async (
  formUrl: string
): Promise<UniversalRULFormResponse> => {
  const response = await apiClient.get<UniversalRULFormResponse>(formUrl);
  return response.data;
};

/**
 * Update field - uses patchUrl from __form_meta or constructs from base URL
 *
 * @param formUrl - Base form URL (used as fallback)
 * @param fieldName - Field to update
 * @param value - New value
 * @param formState - State values for fields (preserves values across stateless PATCH requests)
 * @param patchUrlPattern - URL pattern from __form_meta.patch_url (e.g., "/forms/.../field/{field_name}")
 * @returns Updated form structure
 */
export const updateField = async (
  formUrl: string,
  fieldName: string,
  value: any,
  formState?: Record<string, any>,
  patchUrlPattern?: string
): Promise<UniversalRULUpdateResponse> => {
  // Use pattern from __form_meta if available, otherwise fallback to constructed URL
  // Remove query params from formUrl for fallback (they should only be in initial GET)
  const baseFormUrl = formUrl.split('?')[0];
  const url = patchUrlPattern
    ? patchUrlPattern.replace('{field_name}', fieldName)
    : `${baseFormUrl}/field/${fieldName}`;

  const response = await apiClient.patch<UniversalRULUpdateResponse>(url, {
    value,
    form_state: formState || {},
  });
  return response.data;
};

/**
 * Submit form - uses submitUrl from __form_meta or action.url, or fallback to formUrl/submit
 *
 * @param formUrl - Base form URL (used as fallback)
 * @param data - Form data
 * @param submitUrl - Custom submit URL from __form_meta.submit_url or submit_button.action.url
 * @param method - HTTP method (default: POST)
 * @returns Submit response
 */
export const submitForm = async (
  formUrl: string,
  data: Record<string, any>,
  submitUrl?: string,
  method: string = 'POST'
): Promise<UniversalRULSubmitResponse> => {
  // Use custom submitUrl if provided, otherwise fallback to formUrl/submit
  // Remove query params from formUrl for fallback (they should only be in initial GET)
  const baseFormUrl = formUrl.split('?')[0];
  const url = submitUrl || `${baseFormUrl}/submit`;

  console.log('[RUL] Submit to', url, 'data:', data);

  // Send data directly without wrapper (standard RUL format)
  // Backend expects flat structure: { field1: value1, field2: value2 }
  const response = await apiClient.request<UniversalRULSubmitResponse>({
    method,
    url,
    data,
  });
  return response.data;
};

/**
 * Export as unified service
 */
const universalRULService = {
  getForm,
  updateField,
  submitForm,
};

export default universalRULService;
